/**
 * ProductionGantt.jsx
 * 
 */

import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react";
// TEMPORARY REMOVED AS PART OF 19.2 UPGRADE
// import { Gantt, ViewMode } from "gantt-task-react";
//import "gantt-task-react/dist/index.css";

import axios from "axios";
import { Button, ButtonGroup, Form, Badge, Modal, Dropdown, Card, Row, Col } from "react-bootstrap";

import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PercentIcon from '@mui/icons-material/Percent';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventIcon from '@mui/icons-material/Event';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CloseIcon from '@mui/icons-material/Close';
import LoopIcon from '@mui/icons-material/Loop';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { StyledEngineProvider } from '@mui/material/styles';
import JobCardModal from "./JobCardModal";
import { toast, Toaster } from "react-hot-toast";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";

const ThemeContext = createContext();

const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('gantt-theme');
    return saved === 'dark';
  });

  const toggleTheme = useCallback(() => {
    setIsDark(prev => {
      const newValue = !prev;
      localStorage.setItem('gantt-theme', newValue ? 'dark' : 'light');
      return newValue;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};


const THEMES = {
  light: {
    background: '#FFFFFF',
    surface: '#F9FAFB',
    border: '#E5E7EB',
    borderDark: '#D1D5DB',
    textPrimary: '#111827',
    textSecondary: '#6B7280',
    textMuted: '#9CA3AF',
    hover: '#F3F4F6',
    selected: '#EEF2FF',
    cardBg: '#FFFFFF',
    modalBg: '#FFFFFF',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6'
  },
  dark: {
    background: '#1F2937',
    surface: '#111827',
    border: '#374151',
    borderDark: '#4B5563',
    textPrimary: '#F9FAFB',
    textSecondary: '#D1D5DB',
    textMuted: '#9CA3AF',
    hover: '#374151',
    selected: '#1E3A8A',
    cardBg: '#374151',
    modalBg: '#1F2937',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#3B82F6'
  }
};

const STATUS_COLORS = {
  Planned: { bar: '#6366F1', progress: '#4F46E5', bg: '#E0E7FF', text: '#312E81' },
  Scheduled: { bar: '#3B82F6', progress: '#2563EB', bg: '#DBEAFE', text: '#1E3A8A' },
  InProduction: { bar: '#10B981', progress: '#059669', bg: '#D1FAE5', text: '#065F46' },
  QC: { bar: '#F59E0B', progress: '#D97706', bg: '#FEF3C7', text: '#92400E' },
  Completed: { bar: '#22C55E', progress: '#16A34A', bg: '#D1FAE5', text: '#166534' },
  OnHold: { bar: '#EF4444', progress: '#DC2626', bg: '#FEE2E2', text: '#991B1B' }
};

const VIEW_MODES = [
  // TEMPORARY REMOVED AS PART OF 19.2 UPGRADE (ViewMode value in mode key within const VIEW_MODES)
  { mode: 
    //ViewMode.Day
    '',
    label: 'Day', icon: CalendarTodayIcon, columnWidth: 65 },
  { mode:
     //ViewMode.Week
     '', 
     
     label: 'Week', icon: DateRangeIcon, columnWidth: 65 },
  { mode:
     //ViewMode.Month
     '',
    
     label: 'Month', icon: EventIcon, columnWidth: 350 }
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

const formatPercent = (value) => {
  return `${(value || 0).toFixed(1)}%`;
};

const formatStatusLabel = (status) => {
  const labels = {
    'InProduction': 'In Production',
    'OnHold': 'On Hold',
    'QC': 'Quality Control'
  };
  return labels[status] || status;
};

const normalizeStatus = (status) => {
  if (!status) return 'Planned';
  return status.replace(/_/g, '');
};

const calculateProgress = (isFullyCompleted, status) => {
  if (isFullyCompleted) return 100;
  const progressMap = {
    'Completed': 100,
    'InProduction': 50,
    'QC': 75,
    'Scheduled': 25,
    'Planned': 0,
    'OnHold': 0
  };
  return progressMap[status] || 0;
};

const getProfitMarginColor = (margin, colors) => {
  if (margin >= 30) return colors.success; // High
  if (margin >= 15) return colors.info;    // Normal
  if (margin > 0) return colors.warning;   // Low
  return colors.danger;                     // Loss
};


const FinancialKPICard = ({ icon: Icon, label, value, subtitle, trend, trendUp, colors }) => (
  <Card style={{
    background: colors.cardBg,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'default',
    height: '100%'
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-2px)';
    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
  }}
  >
    <Card.Body className="p-3">
      <div className="d-flex align-items-start justify-content-between mb-2">
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: `${colors.info}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon style={{ fontSize: 20, color: colors.info }} />
        </div>
        {trend && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            fontWeight: 600,
            color: trendUp ? colors.success : colors.danger
          }}>
            {trendUp ? <TrendingUpIcon style={{ fontSize: 14 }} /> : <TrendingDownIcon style={{ fontSize: 14 }} />}
            {trend}
          </div>
        )}
      </div>
      
      <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
        {label}
      </div>
      
      <div style={{ fontSize: 24, fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2, marginBottom: 4 }}>
        {value}
      </div>
      
      {subtitle && (
        <div style={{ fontSize: 11, color: colors.textSecondary, fontWeight: 500 }}>
          {subtitle}
        </div>
      )}
    </Card.Body>
  </Card>
);


const AIInsightsPanel = ({ insights, colors }) => {
  const [expanded, setExpanded] = useState(false);
  
  const visibleInsights = expanded ? insights : insights.slice(0, 3);
  
  const getInsightIcon = (type) => {
    switch (type) {
      case 'danger': return ErrorOutlineIcon;
      case 'warning': return WarningAmberIcon;
      case 'success': return CheckCircleOutlineIcon;
      default: return LightbulbIcon;
    }
  };
  
  const getInsightColor = (type) => {
    switch (type) {
      case 'danger': return colors.danger;
      case 'warning': return colors.warning;
      case 'success': return colors.success;
      default: return colors.info;
    }
  };

  if (insights.length === 0) return null;

  return (
    <Card style={{
      background: colors.cardBg,
      border: `1px solid ${colors.border}`,
      borderRadius: 12,
      marginBottom: 20
    }}>
      <Card.Body className="p-3">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center gap-2">
            <div style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: `${colors.info}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <LightbulbIcon style={{ fontSize: 18, color: colors.info }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
                AI Recommendations
              </div>
              <div style={{ fontSize: 11, color: colors.textSecondary }}>
                Cost optimization insights
              </div>
            </div>
          </div>
          <Badge bg="primary" pill style={{ fontSize: 11, fontWeight: 600 }}>
            {insights.length}
          </Badge>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visibleInsights.map((insight, idx) => {
            const Icon = getInsightIcon(insight.type);
            const iconColor = getInsightColor(insight.type);
            
            return (
              <div
                key={idx}
                style={{
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderLeft: `3px solid ${iconColor}`,
                  borderRadius: 8,
                  padding: 12
                }}
              >
                <div className="d-flex gap-2">
                  <Icon style={{ fontSize: 18, color: iconColor, flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, marginBottom: 4 }}>
                      {insight.title}
                    </div>
                    <div style={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.5, marginBottom: 6 }}>
                      {insight.message}
                    </div>
                    {insight.details && insight.details.length > 0 && (
                      <div style={{ fontSize: 10, color: colors.textMuted, paddingLeft: 12, borderLeft: `2px solid ${colors.border}` }}>
                        {insight.details.map((detail, i) => (
                          <div key={i} style={{ marginBottom: 2 }}>• {detail}</div>
                        ))}
                      </div>
                    )}
                    {insight.suggestion && (
                      <div style={{
                        marginTop: 8,
                        padding: 8,
                        background: `${iconColor}08`,
                        borderRadius: 6,
                        fontSize: 11,
                        color: colors.textPrimary,
                        fontWeight: 600
                      }}>
                        <LightbulbIcon style={{ fontSize: 12, marginRight: 4, verticalAlign: 'middle' }} />
                        {insight.suggestion}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {insights.length > 3 && (
          <Button
            variant="link"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            style={{ fontSize: 12, fontWeight: 600, padding: '8px 0', marginTop: 8 }}
          >
            {expanded ? 'Show Less' : `View ${insights.length - 3} More Recommendations`}
          </Button>
        )}
      </Card.Body>
    </Card>
  );
};

const ThemeToggle = () => {
  const { isDark, toggleTheme } = useTheme();
  
  return (
    <Button
      variant="outline-secondary"
      onClick={toggleTheme}
      style={{
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        fontWeight: 600
      }}
    >
      {isDark ? <LightModeIcon style={{ fontSize: 16 }} /> : <DarkModeIcon style={{ fontSize: 16 }} />}
      {isDark ? 'Light' : 'Dark'}
    </Button>
  );
};


const ExportMenu = ({ tasks, colors }) => {
  const [loading, setLoading] = useState(false);

  const exportToPDF = async () => {
    setLoading(true);
    try {
      const element = document.getElementById('gantt-chart-container');
      const canvas = await html2canvas(element, { backgroundColor: colors.background, scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`production-gantt-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    try {
      const data = tasks.map(task => ({
        'Order Number': task.cardData.orderNumber,
        'Project': task.cardData.projectName,
        'Product': task.cardData.productName,
        'Customer': task.cardData.customer,
        'Machine': task.cardData.machineCode,
        'Department': task.cardData.department,
        'Status': formatStatusLabel(task.cardData.status),
        'Start Date': task.start.toISOString().split('T')[0],
        'End Date': task.end.toISOString().split('T')[0],
        'Progress %': task.progress,
        'Quantity': task.cardData.qtyRequired,
        'Cost': task.cost?.totalCost || 0,
        'Revenue': task.cost?.totalRevenue || 0,
        'Profit': task.cost?.totalProfit || 0,
        'Margin %': task.cost?.profitMargin || 0
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Production Schedule');
      XLSX.writeFile(wb, `production-schedule-${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Excel exported successfully');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error('Failed to export Excel');
    }
  };

  return (
    <Dropdown>
      <Dropdown.Toggle 
        variant="outline-secondary"
        disabled={loading}
        style={{
          fontSize: 13,
          fontWeight: 600,
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 6
        }}
      >
        {loading ? <LoopIcon className="spinner" style={{ fontSize: 16 }} /> : <FileDownloadIcon style={{ fontSize: 16 }} />}
        Export
      </Dropdown.Toggle>

      <Dropdown.Menu style={{ background: colors.surface, borderColor: colors.border }}>
        <Dropdown.Item onClick={exportToPDF} style={{ color: colors.textPrimary }}>
          <PictureAsPdfIcon style={{ fontSize: 14, marginRight: 8 }} />
          Export as PDF
        </Dropdown.Item>
        <Dropdown.Item onClick={exportToExcel} style={{ color: colors.textPrimary }}>
          <GridOnIcon style={{ fontSize: 14, marginRight: 8 }} />
          Export as Excel
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};



const ProductionGanttInner = ({ department }) => {
  const { isDark } = useTheme();
  const colors = isDark ? THEMES.dark : THEMES.light;

  const [tasks, setTasks] = useState([]);
  const [enrichedTasks, setEnrichedTasks] = useState([]);
  const [viewMode, setViewMode] = useState(
    // TEMPORARY REMOVED AS PART OF 19.2 UPGRADE
    //ViewMode.Week
  );
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState([]);
  
  const [selectedCard, setSelectedCard] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [detailedCard, setDetailedCard] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);


  useEffect(() => {
    fetchProductionData();
  }, [department]);


  useEffect(() => {
    if (tasks.length > 0) {
      enrichTasksWithCost();
    }
  }, [tasks]);


  useEffect(() => {
    if (enrichedTasks.length > 0) {
      const generatedInsights = generateAIInsights(enrichedTasks);
      setInsights(generatedInsights);
    }
  }, [enrichedTasks]);

  const fetchProductionData = async () => {
    setLoading(true);
    
    try {
      let apiUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps`;
      if (department && department !== "production") {
        apiUrl += `?department=${encodeURIComponent(department)}`;
      }

      const response = await axios.get(apiUrl, {
        headers: {
          accessToken: localStorage.getItem("accessToken"),
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        const ganttTasks = response.data.data.map((step, index) => {
          const startDate = step.plannedStart ? new Date(step.plannedStart) : new Date();
          const endDate = step.plannedFinish 
            ? new Date(step.plannedFinish) 
            : new Date(startDate.getTime() + 24 * 60 * 60 * 1000);

          const status = normalizeStatus(step.status);
          const statusColors = STATUS_COLORS[status] || STATUS_COLORS.Planned;

          return {
            id: String(step.step_id || index),
            name: `${step.projectNumber} - ${step.customerName}`,
            type: "task",
            start: startDate,
            end: endDate,
            progress: calculateProgress(step.isFullyCompleted, status),
            isDisabled: true,
            
            styles: {
              backgroundColor: statusColors.bar,
              progressColor: statusColors.progress,
              progressSelectedColor: statusColors.progress,
              backgroundSelectedColor: statusColors.bar
            },
            
            cardData: {
              id: step.external_key,
              stepid: step.step_id,
              orderNumber: step.projectNumber,
              projectName: step.projectName,
              productName: step.productName,
              sku: step.skuNumber,
              customer: step.customerName,
              machine: step.machineName,
              machineCode: step.machineCode,
              machineName: step.machineName,
              qtyPlanned: step.qtyRequired,
              qtyRequired: step.qtyRequired,
              status: status,
              department: step.department,
              plannedStart: step.plannedStart,
              plannedFinish: step.plannedFinish,
              isStep: true,
              orderDbId: step.orderDbId,
              itemId: step.itemId,
              pwoid: step.pwo_id,
              externalkey: step.external_key,
              external_key: step.external_key,
              processLocation: step.processLocation,
              holdReason: step.holdReason
            },
          };
        });

        setTasks(ganttTasks);
      }
    } catch (error) {
      console.error("Failed to fetch production steps:", error);
      toast.error('Failed to load production data');
    } finally {
      setLoading(false);
    }
  };

  const enrichTasksWithCost = async () => {
    const uniqueKeys = [...new Set(tasks.map(t => t.cardData.external_key))].filter(Boolean);
    
    const costPromises = uniqueKeys.map(key =>
      axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-details`, {
        params: { external_key: key },
        headers: {
          accessToken: localStorage.getItem("accessToken"),
          "Content-Type": "application/json",
        }
      }).catch(() => null)
    );

    const costResponses = await Promise.all(costPromises);
    
    const costMap = {};
    costResponses.forEach((res, idx) => {
      if (res?.data?.success) {
        const key = uniqueKeys[idx];
        const { costing, charges, meta } = res.data;
        
        const task = tasks.find(t => t.cardData.external_key === key);
        const qty = task?.cardData.qtyRequired || 1;
        
        const deptName = meta.eligible_departments?.[0]?.name || task?.cardData.department;
        const deptCharges = charges.byDepartment?.[deptName] || [];
        const charge = deptCharges[0] || { net: 0, retail: 0 };
        
        const totalCost = charge.net * qty;
        const totalRevenue = charge.retail * qty;
        const totalProfit = totalRevenue - totalCost;
        const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue * 100) : 0;
        
        costMap[key] = {
          costPerUnit: charge.net,
          pricePerUnit: charge.retail,
          profitPerUnit: charge.retail - charge.net,
          totalCost,
          totalRevenue,
          totalProfit,
          profitMargin,
          estMinutes: costing.estMinutesTotal || 0
        };
      }
    });

    const enhanced = tasks.map(task => ({
      ...task,
      cost: costMap[task.cardData.external_key] || {
        costPerUnit: 0,
        pricePerUnit: 0,
        profitPerUnit: 0,
        totalCost: 0,
        totalRevenue: 0,
        totalProfit: 0,
        profitMargin: 0,
        estMinutes: 0
      }
    }));

    setEnrichedTasks(enhanced);
  };

  const generateAIInsights = (tasks) => {
    const insights = [];
    
    // 1. Low Margin Alert
    const lowMarginTasks = tasks.filter(t => t.cost.profitMargin > 0 && t.cost.profitMargin < 15);
    if (lowMarginTasks.length > 0) {
      insights.push({
        type: 'warning',
        priority: 'high',
        title: 'Low Profit Margin Detected',
        message: `${lowMarginTasks.length} jobs with margins below 15%`,
        details: lowMarginTasks.slice(0, 3).map(t => 
          `${t.cardData.orderNumber}: ${t.cost.profitMargin.toFixed(1)}% margin (${formatCurrency(t.cost.totalProfit)} profit)`
        ),
        suggestion: 'Consider renegotiating pricing or optimizing production costs'
      });
    }
    
    // 2. Loss-Making Jobs
    const lossMakingTasks = tasks.filter(t => t.cost.totalProfit < 0);
    if (lossMakingTasks.length > 0) {
      const totalLoss = lossMakingTasks.reduce((sum, t) => sum + Math.abs(t.cost.totalProfit), 0);
      insights.push({
        type: 'danger',
        priority: 'critical',
        title: 'Loss-Making Jobs Alert',
        message: `${lossMakingTasks.length} jobs losing money (${formatCurrency(totalLoss)} total loss)`,
        details: lossMakingTasks.map(t => 
          `${t.cardData.orderNumber}: ${formatCurrency(t.cost.totalProfit)} loss`
        ),
        suggestion: 'Urgent: Review pricing or cancel unprofitable orders'
      });
    }
    
    // 3. High-Margin Opportunities
    const highMarginTasks = tasks.filter(t => t.cost.profitMargin > 30);
    if (highMarginTasks.length > 0) {
      const totalProfit = highMarginTasks.reduce((sum, t) => sum + t.cost.totalProfit, 0);
      insights.push({
        type: 'success',
        priority: 'info',
        title: 'High-Margin Opportunities',
        message: `${highMarginTasks.length} jobs with excellent margins (${formatCurrency(totalProfit)} profit)`,
        details: [
          `Average margin: ${(highMarginTasks.reduce((sum, t) => sum + t.cost.profitMargin, 0) / highMarginTasks.length).toFixed(1)}%`
        ],
        suggestion: 'Focus sales efforts on similar high-value customers'
      });
    }
    
    // 4. At-Risk Deadlines
    const atRiskTasks = tasks.filter(t => {
      const daysRemaining = Math.ceil((t.end - new Date()) / (1000 * 60 * 60 * 24));
      return daysRemaining <= 2 && daysRemaining > 0 && t.progress < 80;
    });
    if (atRiskTasks.length > 0) {
      insights.push({
        type: 'warning',
        priority: 'high',
        title: 'Deadlines At Risk',
        message: `${atRiskTasks.length} jobs may miss deadlines`,
        details: atRiskTasks.slice(0, 3).map(t => 
          `${t.cardData.orderNumber}: ${t.progress}% complete, due soon`
        ),
        suggestion: 'Consider expediting or notifying customers'
      });
    }
    
    return insights.sort((a, b) => {
      const priority = { critical: 0, high: 1, medium: 2, info: 3 };
      return priority[a.priority] - priority[b.priority];
    });
  };

  const handleTaskClick = useCallback((task) => {
    const taskData = enrichedTasks.find(t => t.id === task.id);
    if (taskData?.cardData) {
      setSelectedCard(taskData.cardData);
      setShowModal(true);
      fetchCardDetails(taskData.cardData.external_key);
    }
  }, [enrichedTasks]);

  const fetchCardDetails = async (externalKey) => {
    setModalLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-details?external_key=${externalKey}`,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      if (response.data.success && response.data.data.length > 0) {
        setDetailedCard(response.data.data[0]);
      }
    } catch (error) {
      console.error("Failed to fetch card details:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const financialStats = useMemo(() => {
    const totalRevenue = enrichedTasks.reduce((sum, t) => sum + (t.cost?.totalRevenue || 0), 0);
    const totalCost = enrichedTasks.reduce((sum, t) => sum + (t.cost?.totalCost || 0), 0);
    const totalProfit = totalRevenue - totalCost;
    const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue * 100) : 0;
    
    return { totalRevenue, totalCost, totalProfit, avgMargin };
  }, [enrichedTasks]);

  if (loading) {
    return (
      <div style={{ minHeight: '600px', background: colors.background, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <LoopIcon className="spinner" style={{ fontSize: 48, color: colors.info, marginBottom: 16 }} />
          <p style={{ color: colors.textSecondary, fontSize: 14, fontWeight: 600 }}>Loading production schedule...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: colors.background, borderRadius: 12, border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: isDark ? "#1F2937" : "#fff",
            color: isDark ? "#F9FAFB" : "#111827",
          },
        }}
      />


      <div style={{ background: colors.surface, borderBottom: `1px solid ${colors.border}`, padding: 24 }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: colors.textPrimary }}>
              Production Cost Timeline
              <Badge bg="secondary" style={{ marginLeft: 12, fontSize: 10, fontWeight: 600, padding: '4px 8px' }}>
                ANALYTICS
              </Badge>
            </h4>
            <p style={{ margin: '6px 0 0 0', color: colors.textSecondary, fontSize: 14 }}>
              {department && department !== "production" ? `${department} Department` : "All Departments"} · {enrichedTasks.length} Tasks
            </p>
          </div>

          <div className="d-flex gap-2">
            <ExportMenu tasks={enrichedTasks} colors={colors} />
            <ThemeToggle />
          </div>
        </div>


        <Row className="g-3 mb-3">
          <Col md={3}>
            <FinancialKPICard
              icon={AttachMoneyIcon}
              label="Total Revenue"
              value={formatCurrency(financialStats.totalRevenue)}
              colors={colors}
            />
          </Col>
          <Col md={3}>
            <FinancialKPICard
              icon={TrendingDownIcon}
              label="Total Cost"
              value={formatCurrency(financialStats.totalCost)}
              colors={colors}
            />
          </Col>
          <Col md={3}>
            <FinancialKPICard
              icon={TrendingUpIcon}
              label="Gross Profit"
              value={formatCurrency(financialStats.totalProfit)}
              subtitle={`${enrichedTasks.length} jobs`}
              colors={colors}
            />
          </Col>
          <Col md={3}>
            <FinancialKPICard
              icon={PercentIcon}
              label="Profit Margin"
              value={formatPercent(financialStats.avgMargin)}
              subtitle={financialStats.avgMargin > 25 ? 'Excellent' : financialStats.avgMargin > 15 ? 'Good' : 'Needs Improvement'}
              colors={colors}
            />
          </Col>
        </Row>

    
        <AIInsightsPanel insights={insights} colors={colors} />

    
        <ButtonGroup size="sm">
          {VIEW_MODES.map(({ mode, label, icon: Icon }) => (
            <Button
              key={mode}
              variant={viewMode === mode ? "primary" : "outline-secondary"}
              onClick={() => setViewMode(mode)}
              style={{
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <Icon style={{ fontSize: 14 }} />
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </div>


      <div id="gantt-chart-container" style={{ background: colors.background, padding: 20 }}>
        {enrichedTasks.length > 0 ? (
          {/*
            
          // TEMPORARY REMOVED AS PART OF 19.2 UPGRADE
  
          <Gantt
            tasks={enrichedTasks}
            viewMode={viewMode}
            onClick={handleTaskClick}
            columnWidth={VIEW_MODES.find(v => v.mode === viewMode).columnWidth}
            listCellWidth="220px"
            rowHeight={52}
            barCornerRadius={6}
            barFill={75}
            todayColor="rgba(59, 130, 246, 0.12)"
            fontSize="13px"
            fontFamily="'Inter', -apple-system, system-ui, sans-serif"
          />*/}
        ) : (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <ScheduleIcon style={{ fontSize: 48, color: colors.textMuted, marginBottom: 16 }} />
            <p style={{ color: colors.textSecondary, fontSize: 16 }}>No production tasks scheduled</p>
          </div>
        )}
      </div>

    
      {showModal && selectedCard && (
        <JobCardModal
          open={showModal}
          onClose={() => setShowModal(false)}
          card={selectedCard}
          detailedCard={detailedCard}
          modalLoading={modalLoading}
          onPatchCard={fetchProductionData}
          orderMeta={detailedCard?.meta}
          orderCosting={detailedCard?.costing}
          orderCharges={detailedCard?.charges}
        />
      )}

      <style>{`
        .spinner {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const ProductionGantt = (props) => (
  <StyledEngineProvider injectFirst>
    (<ThemeProvider>
      <ProductionGanttInner {...props} />
    </ThemeProvider>)
  </StyledEngineProvider>
);

export default ProductionGantt;
